import React, { useCallback, useEffect, useRef, useState } from "react";
import { Button, Spin, Table, Tag, Tooltip, message } from "antd";
import { KeyRound, RefreshCw } from "lucide-react";
import { AN_COLORS, AN_FONT, AN_MONO } from "../../../../design-system/tokens";
import { anTableProps, fmtDate, tableEmpty } from "../../../../design-system/tableUi";
import { listGrants, syncGrants } from "../../../../service/sshProxyService";
import { Note } from "../sshProxyUi";

// SSH grants: the key an Endpoint Policy holds on one account of one endpoint.
// The backend creates and ends them as policies are saved, suspended and
// deleted; this panel shows where each one stands.
//
// Two modes:
//   policyId   — the grants issued under one policy (policy row / after save),
//                with an admin "Re-sync grants" button.
//   instanceId — every grant on one endpoint (the endpoint's Grants tab).

const STATUS = {
  pending: { color: "gold", label: "Installing key…", hint: "The gateway hasn't installed it yet. This normally takes under a minute." },
  active: { color: "green", label: "Active", hint: "The key is installed and in the vault." },
  failed: { color: "red", label: "Failed", hint: "The install job gave up. Re-sync grants to try again." },
  disabled: { color: "default", label: "Ended", hint: "This grant has ended and its key is no longer held." },
};

// Re-sync's skipped entries ("host 10.0.0.9", "account root", "endpoint 21 has
// no gateway"), each with what it means and what to do.
const explainSkipped = (item) => {
  // The backend sends the bare item; drop any reason already attached so the
  // explanation isn't doubled.
  // (A host may carry a port, so only these known suffixes are removed.)
  const s = String(item).replace(/: (unknown|not allowed)$/i, "");
  if (/^host /i.test(s)) return `${s}: no onboarded endpoint matches that IP or hostname.`;
  if (/^account /i.test(s)) return `${s}: the account isn't allowed (root, authnull-svc, nobody, or an invalid name).`;
  if (/^endpoint .* has no gateway/i.test(s)) return `${s}: assign a gateway to that endpoint on the endpoint page, then re-sync.`;
  return s;
};

// While anything is pending, and after a save (which returns only the policy
// id, before its keys exist), re-read every 5 seconds — for 2 minutes at most.
const POLL_MS = 5000;
const POLL_MAX = 24;

export default function GrantsPanel({
  policyId,
  instanceId,
  endpointName, // (endpointId) => label
  policyName, // (policyId) => label — only a fallback: listGrants sends grant.policyName
  canSync = false,
  justSaved = false,
  compact = false,
}) {
  const [grants, setGrants] = useState(null);
  const [error, setError] = useState("");
  const [messageApi, msgCtx] = message.useMessage();
  const [syncing, setSyncing] = useState(false);
  const [syncResult, setSyncResult] = useState(null);
  const polls = useRef(0);
  const timer = useRef(null);

  const load = useCallback(() => {
    listGrants({
      policyId,
      instanceId,
      onSuccess: (rows) => {
        setError("");
        setGrants(rows);
      },
      onFailure: (err) => {
        setError(err?.response?.data?.error || "Couldn't load the grants.");
        setGrants((g) => g || []);
      },
    });
  }, [policyId, instanceId]);

  useEffect(() => {
    polls.current = 0;
    setGrants(null);
    load();
    return () => clearTimeout(timer.current);
  }, [load]);

  // Poll while pending, or while a just-saved policy has no grants yet.
  useEffect(() => {
    if (grants === null) return undefined;
    const pending = grants.some((g) => g.status === "pending");
    const waitingAfterSave = justSaved && grants.length === 0;
    if ((pending || waitingAfterSave) && polls.current < POLL_MAX) {
      timer.current = setTimeout(() => {
        polls.current += 1;
        load();
      }, POLL_MS);
    }
    return () => clearTimeout(timer.current);
  }, [grants, justSaved, load]);

  const resync = () => {
    setSyncing(true);
    setSyncResult(null);
    syncGrants({
      policyId,
      onSuccess: (res) => {
        setSyncing(false);
        setSyncResult(res);
        messageApi.success(`Grants re-synced: ${res.created || 0} created, ${res.kept || 0} kept, ${res.disabled || 0} ended`);
        polls.current = 0;
        load();
      },
      onFailure: (err) => {
        setSyncing(false);
        const status = err?.response?.status;
        setSyncResult({
          error:
            status === 403
              ? "Administrator role required"
              : status === 404
                ? "Policy not found"
                : err?.response?.data?.error || "Couldn't sync the grants",
        });
      },
    });
  };

  // Disabled grants are history; show the live ones first.
  const order = { failed: 0, pending: 1, active: 2, disabled: 3 };
  const rows = [...(grants || [])].sort((a, b) => (order[a.status] ?? 9) - (order[b.status] ?? 9));

  const columns = [
    ...(policyId
      ? [{ title: "Host", key: "endpoint", render: (_, g) => <span style={{ fontSize: 12.5 }}>{endpointName ? endpointName(g.endpointId) : `Endpoint #${g.endpointId}`}</span> }]
      : [{ title: "Policy", key: "policy", render: (_, g) => <span style={{ fontSize: 12.5 }}>{g.policyName || (policyName ? policyName(g.policyId) : g.policyId || "—")}</span> }]),
    { title: "Account", key: "account", render: (_, g) => <span style={{ fontFamily: AN_MONO, fontSize: 12 }}>{g.account}</span> },
    {
      title: "Status",
      key: "status",
      render: (_, g) => {
        const s = STATUS[g.status] || { color: "default", label: g.status || "—" };
        return (
          <span style={{ display: "inline-flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
            <Tooltip title={s.hint}>
              <Tag color={s.color} style={{ marginInlineEnd: 0, display: "inline-flex", alignItems: "center", gap: 6 }}>
                {g.status === "pending" && <Spin size="small" />}
                {s.label}
              </Tag>
            </Tooltip>
            {g.status === "disabled" && g.disabledReason && (
              <span style={{ fontSize: 12, color: AN_COLORS.muted }}>{g.disabledReason}</span>
            )}
          </span>
        );
      },
    },
    {
      title: "Key fingerprint",
      key: "fp",
      // Ended keys keep their fingerprint: it's what an auditor matches against
      // the host's auth.log. Greyed, as it's no longer held.
      render: (_, g) =>
        (g.status === "active" || g.status === "disabled") && g.keyFingerprint ? (
          <span
            style={{
              fontFamily: AN_MONO,
              fontSize: 11.5,
              wordBreak: "break-all",
              ...(g.status === "disabled" ? { color: AN_COLORS.muted } : null),
            }}
          >
            {g.keyFingerprint}
          </span>
        ) : (
          <span style={{ color: AN_COLORS.muted }}>—</span>
        ),
    },
    {
      title: "Since",
      key: "since",
      width: 170,
      render: (_, g) => (
        <span style={{ fontSize: 12 }}>{fmtDate(g.disabledAt || g.activatedAt || g.createdAt)}</span>
      ),
    },
  ];

  const pendingCount = (grants || []).filter((g) => g.status === "pending").length;

  return (
    <div style={{ display: "grid", gap: 10, fontFamily: AN_FONT }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, flexWrap: "wrap" }}>
        <span style={{ display: "inline-flex", alignItems: "center", gap: 7, fontSize: compact ? 12.5 : 13, fontWeight: 600, color: AN_COLORS.ink }}>
          <KeyRound size={14} color={AN_COLORS.muted} />
          {policyId ? "Grants issued under this policy" : "Grants on this endpoint"}
          {pendingCount > 0 && <span style={{ fontWeight: 400, color: AN_COLORS.muted }}>· {pendingCount} being installed…</span>}
        </span>
        <span style={{ display: "inline-flex", gap: 8 }}>
          <Button size="small" onClick={() => { polls.current = 0; load(); }}>Refresh</Button>
          {canSync && policyId && (
            <Tooltip title="Re-run this policy's grant sync — the retry when something failed at save time.">
              <Button size="small" loading={syncing} onClick={resync} icon={<RefreshCw size={12} style={{ verticalAlign: -1, marginRight: 4 }} />}>
                Re-sync grants
              </Button>
            </Tooltip>
          )}
        </span>
      </div>

      {msgCtx}
      {/* Counts go in a toast; skipped items stay on screen, each explained. */}
      {syncResult?.error && <Note tone="danger">{syncResult.error}</Note>}
      {(syncResult?.skipped || []).map((item) => (
        <Note key={item} tone="warn">{explainSkipped(item)}</Note>
      ))}

      {error && <Note tone="danger">{error}</Note>}

      <Table
        {...anTableProps}
        size="small"
        rowKey={(g) => g.id}
        columns={columns}
        dataSource={rows}
        loading={grants === null}
        pagination={rows.length > 10 ? { pageSize: 10, size: "small" } : false}
        locale={tableEmpty(
          grants === null,
          justSaved
            ? "No grants yet — they appear a few seconds after saving."
            : policyId
              ? "No grants under this policy. Grants are issued only for the hosts and accounts it names — hosts that are onboarded endpoints with a gateway."
              : "No grants have been issued on this endpoint.",
        )}
        scroll={{ x: true }}
      />
    </div>
  );
}
